import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { router, useGlobalSearchParams, usePathname } from 'expo-router';
import React, { useEffect } from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTerms } from '@/hooks/use-terms';
import { useThemeColor } from '@/hooks/use-theme-color';
import {
  getActiveBottomNavIndex,
  getBottomNavItemsForRole,
  isBottomNavHiddenRoute,
  MobileBottomNavItem,
} from '@/lib/mobile-bottom-nav';
import { useAuthStore } from '@/store/authStore';
import { useSidebar } from '@/store/sidebarStore';

import { ThemedText } from './themed-text';
import { useColorScheme } from '@/hooks/use-color-scheme';

const TAB_BAR_PADDING_TOP = 8;
const TAB_BAR_CONTENT_HEIGHT = 56;
const ACTIVE_PILL_BACKGROUND = '#FFF4ED'; // primary-50

export const MOBILE_BOTTOM_NAV_HEIGHT = TAB_BAR_PADDING_TOP + TAB_BAR_CONTENT_HEIGHT;

const PILL_TRANSITION = { duration: 220, easing: Easing.out(Easing.cubic) };

type BottomNavItemProps = {
  item: MobileBottomNavItem;
  isActive: boolean;
  activeColor: string;
  inactiveColor: string;
  onPress: () => void;
};

function BottomNavItem({
  item,
  isActive,
  activeColor,
  inactiveColor,
  onPress,
}: BottomNavItemProps) {
  const isDark = useColorScheme() === 'dark';
  const progress = useSharedValue(isActive ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(isActive ? 1 : 0, PILL_TRANSITION);
  }, [isActive, progress]);

  const pillStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [{ scaleX: 0.6 + progress.value * 0.4 }],
  }));

  const iconStyle = useAnimatedStyle(() => ({
    transform: [{ scale: 1 + progress.value * 0.08 }],
  }));

  const terms = useTerms();
  const label = item.term ? terms.title[item.term] : item.label;
  const color = isActive ? activeColor : inactiveColor;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isActive }}
      className="min-w-0 flex-1 items-center justify-center gap-1 px-1 py-1.5 active:opacity-80"
    >
      <View className="h-8 w-16 items-center justify-center">
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: 16,
              // No escuro a pílula é laranja cheia com ícone branco (como os círculos de ícone).
              backgroundColor: isDark ? activeColor : ACTIVE_PILL_BACKGROUND,
            },
            pillStyle,
          ]}
        />
        <Animated.View style={iconStyle}>
          <Ionicons
            name={isActive ? item.iconActive : item.icon}
            size={22}
            color={isActive && isDark ? '#FFFFFF' : color}
          />
        </Animated.View>
      </View>

      <ThemedText numberOfLines={1} className="font-semibold text-[11px]" style={{ color }}>
        {label}
      </ThemedText>
    </Pressable>
  );
}

export function MobileBottomNav() {
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const { openSidebar } = useSidebar();
  const { user } = useAuthStore();

  const items = getBottomNavItemsForRole(user, user?.membership);
  const activeIndex = getActiveBottomNavIndex(pathname, items);

  const activeColor = useThemeColor('tint');
  const inactiveColor = useThemeColor('icon');
  const backgroundColor = useThemeColor('card');

  const handlePress = (index: number) => {
    if (process.env.EXPO_OS === 'ios') {
      void Haptics.selectionAsync();
    }
    const item = items[index];
    if (item.action === 'menu') {
      openSidebar();
      return;
    }
    if (item.href) {
      router.push(item.href);
    }
  };

  return (
    <View
      style={{
        paddingTop: TAB_BAR_PADDING_TOP,
        paddingBottom: insets.bottom,
        backgroundColor,
        boxShadow: '0 -1px 0 rgba(23, 17, 10, 0.06), 0 -8px 24px rgba(23, 17, 10, 0.05)',
      }}
    >
      <View className="flex-row items-center" style={{ height: TAB_BAR_CONTENT_HEIGHT }}>
        {items.map((item, index) => (
          <BottomNavItem
            key={item.id}
            item={item}
            isActive={index === activeIndex}
            activeColor={activeColor}
            inactiveColor={inactiveColor}
            onPress={() => handlePress(index)}
          />
        ))}
      </View>
    </View>
  );
}

/** Bottom nav aparece: mobile e fora dos formulários de criar/editar. */
export function useMobileBottomNavVisible(): boolean {
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const { edit } = useGlobalSearchParams<{ edit?: string }>();
  return width < 768 && !isBottomNavHiddenRoute(pathname, edit);
}

/**
 * Folga de baixo que o rodapé fixo de um formulário soma ao próprio padding:
 * sem o bottom nav, o fundo do rodapé tem que descer até a borda da tela
 * (home indicator) — padding no layout deixaria uma faixa cinza embaixo.
 */
export function useFormFooterInset(): number {
  const insets = useSafeAreaInsets();
  return useMobileBottomNavVisible() ? 0 : insets.bottom;
}

export function useMobileBottomNavInset(): number {
  const insets = useSafeAreaInsets();
  return MOBILE_BOTTOM_NAV_HEIGHT + insets.bottom;
}
